import { ActionTree, GetterTree, Module, MutationTree } from "vuex"
import axios from "axios"
import { RootState } from "."
import { OemDeviceBackup } from "@/types/oem-device"

export interface OemDeviceBackupsState {
    loading: boolean
    activeOemDeviceId: number
    backupList: OemDeviceBackup[]
    abortController: AbortController | null
    downloadingBackupIds: number[]
}

export interface OemDeviceBackupsApiResponse {
    data: {
        data: OemDeviceBackup[]
    }
}

const state: OemDeviceBackupsState = {
    loading: true,
    activeOemDeviceId: 0,
    backupList: [],
    abortController: null,
    downloadingBackupIds: [],
}

const mutations: MutationTree<OemDeviceBackupsState> = {
    setLoading(state, value) {
        state.loading = value
    },
    setActiveOemDeviceId(state, id) {
        state.activeOemDeviceId = id
    },
    setOemDeviceBackups(state, payload: { id: number; backups: OemDeviceBackup[] }) {
        state.backupList = payload.backups
    },
    setAbortController(state, controller: AbortController | null) {
        state.abortController = controller
    },
    setBackupDownloading(state, payload: { backupId: number; isDownloading: boolean }) {
        if (payload.isDownloading) {
            state.downloadingBackupIds = [...state.downloadingBackupIds, payload.backupId]
        } else {
            state.downloadingBackupIds = state.downloadingBackupIds.filter(
                (id) => id !== payload.backupId,
            )
        }
    },
}

const actions: ActionTree<OemDeviceBackupsState, RootState> = {
    setActiveOemDeviceId({ commit }, id) {
        commit("setActiveOemDeviceId", id)
    },
    async fetchOemDeviceBackups({ commit }, controllerId: number) {
        commit("setLoading", true)

        if (state.abortController) {
            state.abortController.abort()
        }

        const abortController = new AbortController()
        commit("setAbortController", abortController)

        try {
            const url = "/backups/controllers/" + controllerId
            const response: OemDeviceBackupsApiResponse = await axios.get(url, {
                signal: abortController.signal,
            })
            commit("setOemDeviceBackups", { id: controllerId, backups: response.data.data ?? [] })
        } catch (error) {
            console.log("Failed to fetch backups:", error)
        } finally {
            commit("setLoading", false)
        }
    },
    async deleteBackupConfiguration(
        { commit },
        payload: { controllerId: number; backupId: number },
    ) {
        commit("setLoading", true)

        try {
            const url = "/backups/controllers/" + payload.controllerId + "/" + payload.backupId
            const response: OemDeviceBackupsApiResponse = await axios.delete(url)
            commit("setOemDeviceBackups", {
                id: payload.controllerId,
                backups: response.data.data,
            })
        } catch (error) {
            console.log("Failed to delete backup:", error)
        } finally {
            commit("setLoading", false)
        }
    },
    async downloadBackupConfiguration(
        { commit, dispatch },
        payload: { controllerId: number; backupId: number },
    ) {
        commit("setBackupDownloading", { backupId: payload.backupId, isDownloading: true })

        try {
            const url =
                "/backups/controllers/" + payload.controllerId + "/" + payload.backupId + "/download"
            const response = await axios.get(url, { responseType: "blob" })

            const contentDisposition = response.headers["content-disposition"]
            const filename = contentDisposition?.match(/filename="?([^"]+)"?/)?.[1] ?? "backup"

            const objectUrl = URL.createObjectURL(response.data)
            const link = document.createElement("a")
            link.href = objectUrl
            link.download = filename
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(objectUrl)
        } catch (error) {
            console.log("Failed to download backup:", error)
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "controller.download_backup_error",
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )
        } finally {
            commit("setBackupDownloading", { backupId: payload.backupId, isDownloading: false })
        }
    },
}

const getters: GetterTree<OemDeviceBackupsState, RootState> = {
    backupList: (state) => state.backupList,
    loading: (state) => state.loading,
    isBackupDownloading: (state) => (backupId: number) =>
        state.downloadingBackupIds.includes(backupId),
}

const oemDeviceBackupsModule: Module<OemDeviceBackupsState, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}

export default oemDeviceBackupsModule
